/**
 * ENG-8363 — WHY an MCP server config could not be read, and what verdict that
 * earns.
 *
 * Before this module the two config readers in connectivity-probe-context.ts
 * collapsed three genuinely different conditions into a bare `null`, and the two
 * probe legs then disagreed about what that null meant: the HTTP leg called it
 * `transient_error`, the stdio leg called it `down` — for the identical input.
 * The stdio leg carried the reasoning in its own comment ("a missing server
 * entry is a real `down` — the tools aren't wired") and the HTTP leg contradicted
 * it. Neither was right on its own, because `null` was three different facts:
 *
 *   1. `file-unreadable` — `.mcp.json` missing or unparseable. Could be a
 *      read-during-write race, or an agent whose config has never been written.
 *      Never a statement about the integration. Always retryable.
 *   2. `key-absent` — the file parsed, but carries no entry under this key.
 *      AMBIGUOUS BY TIME: permanent if the writer will never emit that key
 *      (a derivation mismatch, an integration the adapter skips), but
 *      GUARANTEED-TRANSIENT right after an integration is added.
 *   3. `wrong-shape` — the entry exists but is not url-bearing (http) /
 *      command-bearing (stdio). The writer produced something this leg cannot
 *      probe: a real wiring fault, not a timing artifact.
 *
 * WHY `key-absent` CANNOT SIMPLY BE `down` (the trap ENG-8355 declined to walk
 * into, and the reason this issue's own suggested direction needed amending):
 * the manager probes at step 6b-i.5 of the poll but writes `.mcp.json` further
 * down the SAME poll, in `writeIntegrations`. A never-probed row is always due
 * (connectivity-probe-runner `isDue`: no `last_connectivity_check_at` ⇒ due). So
 * the FIRST probe of EVERY newly-added integration necessarily runs against a
 * `.mcp.json` that does not contain its key yet. A flat "absent ⇒ down" would
 * therefore fire on every single integration add — and worse, it would break
 * ENG-7575's first-connect backoff (`computeFirstConnectUnsettled`): the
 * deferral would stop engaging and bind-remediation would go back to
 * force-respawning to bind tools that cannot bind yet — the exact ENG-7560 churn
 * loop that backoff exists to prevent.
 *
 * Hence the verdict is age-aware. Inside the first-connect window a missing key
 * is the designed state and reads `unverified`; outside it, the writer has
 * had its chance and the absence is a real `down`.
 *
 * ENG-9034 — WHY THE NON-ESCALATING VERDICT IS `unverified` AND NOT
 * `transient_error`. It was `transient_error`, and `transient_error` is not
 * neutral: it increments `consecutive_connectivity_failures`. A row that stays
 * in one of these no-evidence states — a control-plane integration never wired
 * onto the host, a permanently quarantined row, an unreadable `.mcp.json` —
 * therefore accumulated a failure streak that could only ever grow, because
 * nothing that never gets probed can ever return `ok` to reset it. That is how
 * monday.com on `kim` reached 110 consecutive "failures" against an integration
 * that does not exist there, and how seven more phantom CRITICALs appeared
 * across two agents inside ten minutes. The streak measured the monitor.
 *
 * `unverified` says the same thing honestly and, since ENG-9122, is neutral to
 * the failure counter while carrying its own
 * `consecutive_connectivity_unverified_cycles`. So this does not trade a false
 * alert for silence — a row nothing can probe for hours still becomes visible,
 * as the thing it actually is.
 *
 * The backoff moved with it: `computeFirstConnectUnsettled` accepts `unverified`
 * within the window as well as the legacy `transient_error`. Those two are one
 * change in two files; reverting either alone reintroduces the ENG-7560 churn,
 * and it does so silently. The window is deliberately
 * THE SAME constant ENG-7575's backoff uses ({@link FIRST_CONNECT_WINDOW_MS}) —
 * if the probe escalated to `down` before the backoff stopped deferring, a row
 * would sit in a state where the probe says "broken" and the remediator says
 * "still settling". One constant, imported by both, is what keeps them honest.
 *
 * Fail-open in two places, both load-bearing:
 *   - an unknown/unparseable `created_at` is treated as INSIDE the window. We
 *     escalate to `down` only on positive evidence the row is old; an older API
 *     that stops forwarding `created_at` degrades to the pre-ENG-8363 HTTP
 *     behaviour (permanent transient) rather than to a fleet of false `down`s.
 *   - an `expectedAbsent` row (ENG-7916 quarantine) never escalates at all. A
 *     quarantined integration is REMOVED from `.mcp.json` deliberately, and is
 *     re-probed only so ENG-8036 auto-resume can observe it recover. Its absence
 *     is the precondition of the probe, not a fault to report.
 */
import { readEnvNumber } from './restart-breaker.js';

/** Why a `.mcp.json` server config could not be produced for a probe leg. */
export type McpConfigUnavailableCause =
  /** `.mcp.json` missing or unparseable — says nothing about the integration. */
  | 'file-unreadable'
  /** File parsed; no entry under this server key. */
  | 'key-absent'
  /** Entry present but not url-bearing (http) / command-bearing (stdio). */
  | 'wrong-shape';

/**
 * A config read that either produced a usable config or names WHY it did not.
 * Replaces the bare `T | null` the readers used to return — the null discarded
 * exactly the fact the caller needed to pick an honest verdict.
 */
export type McpConfigLookup<T> =
  | { ok: true; value: T }
  | { ok: false; cause: McpConfigUnavailableCause };

/**
 * ENG-8363 — stamp the quarantine mark onto the rows the manager concatenates
 * into the probe set (ENG-8036 auto-resume). Extracted as a named seam rather
 * than inlined at the call site so a test can drive THIS, not a re-derivation of
 * it: the mark is the only thing standing between a quarantined row and a hard
 * `down` for being in exactly the state quarantine put it in, and an inline
 * `.map()` inside manager-worker is unreachable from a unit test.
 */
export function markQuarantinedForProbe<T extends object>(
  rows: readonly T[],
): Array<T & { quarantined: true }> {
  return rows.map((row) => ({ ...row, quarantined: true as const }));
}

/**
 * Wrap an ENG-8049 INLINE-entry resolve into the same shape the file readers
 * return. On the inline path there is no file and no key lookup, so the only way
 * to fail is the entry not being probeable by this transport — `wrong-shape` is
 * the sole reachable cause, and saying so keeps the legs on one code path.
 */
export function inlineEntryLookup<T>(value: T | null): McpConfigLookup<T> {
  return value ? { ok: true, value } : { ok: false, cause: 'wrong-shape' };
}

/**
 * ENG-7575's first-connect window, owned here so the connectivity probe and
 * bind-remediation cannot drift apart (see the header). manager-worker imports
 * this rather than defining its own copy; the env var name is unchanged, so an
 * operator override still moves both together.
 */
export const FIRST_CONNECT_WINDOW_MS = readEnvNumber(
  'AGT_BIND_REMEDIATION_FIRST_CONNECT_WINDOW_MS',
  900_000, // 15 min
);

/**
 * Is this integration row young enough that a missing `.mcp.json` key is still
 * the expected state? Unknown / unparseable ages answer TRUE (fail-open — see
 * the header: we escalate only on positive evidence of age).
 */
export function isWithinFirstConnectWindow(
  createdAt: string | null | undefined,
  now: number,
  windowMs: number = FIRST_CONNECT_WINDOW_MS,
): boolean {
  if (!createdAt) return true;
  const addedAt = Date.parse(createdAt);
  if (!Number.isFinite(addedAt)) return true;
  return now - addedAt <= windowMs;
}

/**
 * ENG-9718 — the SECOND anchor: has the control plane just reconnected this row?
 *
 * `reconnect_quiesced_until` is a DEADLINE the API bakes at reconnect time and
 * forwards on `/host/agent-integrations`. Compared directly against `now`, with
 * no window constant on this side — deliberately, because the API cannot import
 * `FIRST_CONNECT_WINDOW_MS` from here and a second constant over there would be
 * free to drift from this one. One value, computed once, read by both.
 *
 * Fails CLOSED (absent / unparseable ⇒ false), the opposite of
 * `isWithinFirstConnectWindow`'s fail-open. That asymmetry is intentional: the
 * age test fails open because it cannot establish the row is OLD and must not
 * escalate on a guess, whereas an absent deadline here overwhelmingly means "no
 * reconnect has happened" — it is NULL on every row until one does. Suppressing
 * a `down` on the strength of a value we could not read would turn this into a
 * way to silence failures, which is the one thing it must never become.
 */
export function isWithinReconnectQuiesceWindow(
  reconnectQuiescedUntil: string | null | undefined,
  now: number,
): boolean {
  if (!reconnectQuiescedUntil) return false;
  const deadline = Date.parse(reconnectQuiescedUntil);
  if (!Number.isFinite(deadline)) return false;
  return now < deadline;
}

/**
 * ENG-9718 — is a missing `.mcp.json` key still the EXPECTED state for this row,
 * for either reason?
 *
 * The two anchors answer the same question about different histories. A row is
 * unsettled if it was just created (ENG-8363/ENG-7575) OR just reconnected: both
 * mean the manager has `.mcp.json` work it has not done yet, and in both the
 * absent key is a race with our own writer rather than a fact about the
 * integration.
 *
 * The reason the second anchor was needed at all is that the first is measured
 * from `created_at`, so a row created months ago and reconnected ten seconds ago
 * read as old — which is how a bulk sweep branded 22 rows `down` across four
 * healthy agents on 2026-08-31.
 */
export function isWithinSettlingWindow(
  ctx: { createdAt?: string | null; reconnectQuiescedUntil?: string | null },
  now: number,
  windowMs: number = FIRST_CONNECT_WINDOW_MS,
): boolean {
  return (
    isWithinReconnectQuiesceWindow(ctx.reconnectQuiescedUntil, now) ||
    isWithinFirstConnectWindow(ctx.createdAt, now, windowMs)
  );
}

export interface McpConfigVerdictContext {
  /** The `.mcp.json` key the leg was looking for (for the message). */
  serverKey: string;
  /** Which reader ran — only affects wording, never the verdict. */
  transport: 'http' | 'stdio';
  /** Whether the config came from the file or an ENG-8049 inline entry. */
  source: 'inline config' | '.mcp.json';
  /** The integration row's `created_at`, for the age test. */
  createdAt?: string | null;
  /**
   * ENG-9718 — the row's `reconnect_quiesced_until` deadline, if the control
   * plane has recently reconnected it. The second settling anchor; see
   * {@link isWithinSettlingWindow}.
   */
  reconnectQuiescedUntil?: string | null;
  /**
   * ENG-7916/ENG-8036: this row is QUARANTINED, so its absence from `.mcp.json`
   * is by design. Never escalates — see the header.
   */
  expectedAbsent?: boolean;
  now: number;
  windowMs?: number;
}

/**
 * The single mapping from "why the config was unavailable" to a probe verdict.
 *
 * Both MCP legs (and the Composio account leg) route through this one function
 * on purpose. The bug this issue fixes was two adjacent branches independently
 * deciding what the same condition meant; a shared helper is the only shape that
 * makes "both paths agree" a property of the code rather than of whoever edits
 * it next. Adding a cause here changes every leg at once.
 */
export function verdictForUnavailableMcpConfig(
  cause: McpConfigUnavailableCause,
  ctx: McpConfigVerdictContext,
): { status: 'unverified' | 'down'; message: string } {
  const where = `MCP ${ctx.transport} server '${ctx.serverKey}'`;

  if (cause === 'file-unreadable') {
    // Never the integration's fault: no file, no verdict about the integration.
    //
    // ENG-9034: this branch said exactly that and then returned a verdict anyway.
    // `transient_error` is NOT neutral — it increments
    // `consecutive_connectivity_failures`, and a probe that keeps finding no file
    // keeps incrementing it, so the streak measures the monitor rather than the
    // integration. `unverified` is the honest answer and, since ENG-9122, the
    // neutral one: it has its own `consecutive_connectivity_unverified_cycles`
    // counter, so this trades a false alert for a true one rather than for silence.
    return { status: 'unverified', message: `${where}: ${ctx.source} unreadable or unparseable` };
  }

  if (cause === 'key-absent') {
    if (ctx.expectedAbsent) {
      // ENG-9034: absence is BY DESIGN here. Grading a designed absence as a
      // failure is the clearest case in this function — nothing was observed and
      // nothing was expected to be.
      return {
        status: 'unverified',
        message: `${where}: not in ${ctx.source} (quarantined — absence expected)`,
      };
    }
    if (
      isWithinSettlingWindow(
        { createdAt: ctx.createdAt, reconnectQuiescedUntil: ctx.reconnectQuiescedUntil },
        ctx.now,
        ctx.windowMs,
      )
    ) {
      // ENG-9034: the manager probes BEFORE it writes `.mcp.json` in the same
      // poll, so the first probe of every newly-added integration necessarily
      // finds the key absent. That is a race with our own writer, not a fact
      // about the integration.
      //
      // ENG-7575's first-connect backoff keyed on the `transient_error` this
      // used to return; `computeFirstConnectUnsettled` now accepts `unverified`
      // within the window as well, so the backoff still engages. If you revert
      // one of those two, revert both — the failure mode is the ENG-7560
      // re-respawn churn, and it is silent from here.
      return {
        status: 'unverified',
        message: `${where}: not yet written to ${ctx.source} (${
          isWithinReconnectQuiesceWindow(ctx.reconnectQuiescedUntil, ctx.now)
            ? 'reconnect in progress'
            : 'within first-connect window'
        })`,
      };
    }
    // The one genuine assertion in this function: the row is past its window and
    // still is not wired. That IS a fact about the integration, and it keeps
    // escalating.
    return { status: 'down', message: `${where}: not wired in ${ctx.source}` };
  }

  // wrong-shape: the entry is there but this leg cannot probe it. Not a timing
  // artifact — the writer emitted something unusable — so it escalates. A
  // quarantined row is still exempt: its inline entry is a reconstruction, and a
  // reconstruction we got wrong must not be reported as the integration failing.
  if (ctx.expectedAbsent) {
    // ENG-9034: same reasoning as the quarantined key-absent branch — a
    // reconstruction WE got wrong is not evidence about the integration.
    return {
      status: 'unverified',
      message: `${where}: reconstructed ${ctx.source} entry is not ${ctx.transport}-probeable (quarantined)`,
    };
  }
  return {
    status: 'down',
    message: `${where}: ${ctx.source} entry is not ${ctx.transport}-probeable`,
  };
}
